<template>
    <div class="unified-filter-row datepicker-container" @click="handleContainerClick">
        <e-datepicker
            ref="datepicker"
            :model-value="datePickerValue"
            range
            :placeholder="filter.placeholder || 'Select date range...'"
            icon="calendar"
            :mobile-native="false"
            custom-class="filter-datepicker"
            @range-end="handleDateRangeComplete"
            @update:model-value="handleDateChange"
            @active-change="handleDatePickerToggle"
        />
        <e-button
            v-if="hasValue"
            type="is-white"
            size="is-small"
            class="date-clear-button"
            icon-right="times"
            @click.stop.prevent="clearDateFilter"
            @mousedown.stop
            @mouseup.stop
        />
    </div>
</template>

<script>
export default {
    name: 'DateRangeFilterItem',

    emits: ['update:model-value', 'close-filter-dropdown'],

    props: {
        filter: {
            type: Object,
            required: true,
        },
        modelValue: {
            type: Array,
            default: null,
        },
        applyFilter: {
            type: Function,
            required: true,
        },
        applyFilterImmediate: {
            type: Function,
            required: false,
            default: null,
        },
        expandedSections: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            isDatePickerOpen: false,
            hasCompletedRange: false,
            uniqueId: `datepicker-${Math.random().toString(36).substr(2, 9)}`,
        };
    },

    computed: {
        hasValue() {
            return this.modelValue && this.modelValue.length > 0 && (this.modelValue[0] || this.modelValue[1]);
        },

        datePickerValue() {
            if (!this.modelValue || !Array.isArray(this.modelValue)) {
                return null;
            }

            const plainArray = [...this.modelValue];

            return plainArray;
        },
    },

    mounted() {
        // Add class to parent containers to allow overflow
        this.setupOverflowClasses();

        // Listen for other datepickers opening
        document.addEventListener('datepicker-opened', this.handleOtherDatepickerOpened);

        // Listen for other filter sections being toggled
        document.addEventListener('filter-section-toggled', this.handleFilterSectionToggled);
    },

    beforeUnmount() {
        // Remove overflow classes
        this.removeOverflowClasses();

        // Remove event listeners
        document.removeEventListener('datepicker-opened', this.handleOtherDatepickerOpened);
        document.removeEventListener('filter-section-toggled', this.handleFilterSectionToggled);
    },

    methods: {
        setupOverflowClasses() {
            // Find and mark parent containers to allow datepicker overflow
            const containers = [
                this.$el.closest('.dropdown'),
                this.$el.closest('.dropdown-menu'),
                this.$el.closest('.dropdown-content'),
                this.$el.closest('.filter-dropdown-item'),
                this.$el.closest('.filter-content'),
                this.$el.closest('.filter-section'),
            ];

            containers.forEach(container => {
                if (container) {
                    container.classList.add('has-datepicker-overflow');
                }
            });
        },

        removeOverflowClasses() {
            // Remove overflow classes from parent containers
            const containers = [
                this.$el.closest('.dropdown'),
                this.$el.closest('.dropdown-menu'),
                this.$el.closest('.dropdown-content'),
                this.$el.closest('.filter-dropdown-item'),
                this.$el.closest('.filter-content'),
                this.$el.closest('.filter-section'),
            ];

            containers.forEach(container => {
                if (container) {
                    container.classList.remove('has-datepicker-overflow');
                }
            });
        },

        handleContainerClick(event) {
            // Stop propagation to prevent dropdown from closing when interacting with datepicker
            event.stopPropagation();
        },

        handleDatePickerToggle(isActive) {
            this.isDatePickerOpen = isActive;

            if (isActive) {
                // Increase z-index of the container
                this.$el.style.zIndex = '1010';

                // Notify other datepickers that this one is opening
                const event = new CustomEvent('datepicker-opened', {
                    detail: { id: this.uniqueId },
                });
                document.dispatchEvent(event);

                // Adjust calendar position if it's clipped
                this.$nextTick(() => {
                    this.adjustCalendarPosition();
                });
            } else {
                // Reset container z-index when closed
                this.$el.style.zIndex = '1001';
            }
        },

        handleOtherDatepickerOpened(event) {
            // If another datepicker opened (not this one), close this one
            if (event.detail.id !== this.uniqueId && this.isDatePickerOpen) {
                // Reset z-index before closing
                this.resetCalendarZIndex();

                // Close this datepicker
                if (this.$refs.datepicker) {
                    this.$refs.datepicker.toggle();
                }
            }
        },

        handleFilterSectionToggled(event) {
            // When any other filter section is opened, close this datepicker if it's open
            if (this.isDatePickerOpen) {
                // Reset z-index before closing
                this.resetCalendarZIndex();

                // Close this datepicker
                if (this.$refs.datepicker) {
                    this.$refs.datepicker.toggle();
                }
            }
        },

        resetCalendarZIndex() {
            // Reset z-index of this calendar to default
            const datepicker = this.$el.querySelector('.datepicker');
            if (datepicker) {
                const calendar = datepicker.querySelector('.dropdown-content');
                if (calendar) {
                    calendar.style.zIndex = '9999';
                }
            }
        },

        adjustCalendarPosition() {
            // Find the datepicker calendar within this component
            const datepicker = this.$el.querySelector('.datepicker');
            if (!datepicker) return;

            const calendar = datepicker.querySelector('.dropdown-content');
            if (calendar) {
                const rect = this.$el.getBoundingClientRect();
                const calendarWidth = 360; // Updated to match new calendar width

                // Set high z-index for active calendar
                calendar.style.zIndex = '10000';

                // Default to left positioning (calendar opens to the left)
                // Only switch to right positioning if it would go off-screen to the left
                if (rect.right - calendarWidth < 0) {
                    // Not enough space on the left, position to the right
                    calendar.style.left = '0';
                    calendar.style.right = 'auto';
                } else {
                    // Enough space on the left, keep default left positioning
                    calendar.style.left = 'auto';
                    calendar.style.right = '0';
                }
            }
        },

        handleDateChange(dates) {
            // Emit for two-way binding
            this.$emit('update:model-value', dates);

            // A range is complete once both endpoints are chosen. A same-day
            // range (start === end) is valid — it represents a single inclusive
            // day — so it must still apply and count as an active filter.
            const isCompleteRange =
                dates &&
                Array.isArray(dates) &&
                dates.length === 2 &&
                dates[0] instanceof Date &&
                dates[1] instanceof Date;

            if (isCompleteRange) {
                // Apply filter and close dropdown
                this.applyFilter(this.filter, dates);
                this.$emit('close-filter-dropdown');
            }
        },

        handleDateRangeComplete(dates) {
            // This is called when range selection is complete
            this.hasCompletedRange = true;
        },

        clearDateFilter(event) {
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }

            this.hasCompletedRange = false;

            // Clear the input field
            this.$emit('update:model-value', []);

            // Use applyFilterImmediate if provided and callable, otherwise use applyFilter
            const applyMethod =
                this.applyFilterImmediate && typeof this.applyFilterImmediate === 'function'
                    ? this.applyFilterImmediate
                    : this.applyFilter;

            if (typeof applyMethod === 'function') {
                applyMethod(this.filter, null);
            } else {
                console.error('No valid apply filter method available');
            }
        },
    },
};
</script>

<style scoped>
.datepicker-container {
    display: flex;
    align-items: center;
    width: 100%;
    position: relative;
    overflow: visible !important;
}

.date-clear-button {
    margin-left: 0.5rem;
    border: none !important;
    background: transparent !important;
    color: #666 !important;
    padding: 0.25rem !important;
    min-height: auto !important;
    height: auto !important;
}

.date-clear-button:hover {
    color: #333 !important;
}
</style>
